/* Admin quote requests page for managing customer quote requests
   Updated: Merged Offertes (quote stepper + document list) into this page via tabs */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Breadcrumb from '@/components/admin/Breadcrumb';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader as Loader2, Eye, Trash2, FileText, Mail, Phone, MapPin, Package, Truck, CircleCheck as CheckCircle, Clock, Circle as XCircle, MessageSquare, Sprout, Plus, ArrowLeft, FileSpreadsheet, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { motion } from 'framer-motion';
import DocumentStepper from '@/components/admin/document-stepper/DocumentStepper';
import DocumentList from '@/components/admin/document-stepper/DocumentList';

interface QuoteRequest {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  plant_name: string;
  plant_height: string;
  quantity: number;
  delivery_method: string;
  postal_code: string;
  preferred_contact_method: string;
  optional_message: string | null;
  aanplantservice: boolean;
  status: string;
  created_at: string;
  updated_at: string;
  request_type: string;
  competitor_url: string | null;
  competitor_price: number | null;
  product_variant_id: string | null;
}

const statusColors: Record<string, string> = {
  new: 'bg-[#5a6552]',
  in_progress: 'bg-[#c8a94a]',
  completed: 'bg-[#1e2b22]',
  cancelled: 'bg-[#a24a3d]',
};

const statusLabels: Record<string, string> = {
  new: 'Nieuw',
  in_progress: 'In behandeling',
  completed: 'Voltooid',
  cancelled: 'Geannuleerd',
};

const deliveryMethodLabels: Record<string, string> = {
  binnen_nl: 'Bezorgen binnen Nederland',
  buiten_nl: 'Bezorgen buiten Nederland',
  afhalen: 'Afhalen bij kwekerij',
};

const contactMethodLabels: Record<string, string> = {
  mail: 'E-mail',
  whatsapp: 'WhatsApp',
  gebeld_worden: 'Gebeld worden',
};

interface AdminDocument {
  id: string;
  document_number: string;
  document_type: string;
  customer_name: string;
  customer_email: string;
  total: number;
  status: string;
  created_at: string;
  sent_at: string | null;
}

export default function AdminQuoteRequests() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [quoteRequests, setQuoteRequests] = useState<QuoteRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState<QuoteRequest | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState<QuoteRequest | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') === 'offertes' ? 'offertes' : 'aanvragen');
  const [quoteDocuments, setQuoteDocuments] = useState<AdminDocument[]>([]);
  const [quoteDocsLoading, setQuoteDocsLoading] = useState(true);
  const [showQuoteStepper, setShowQuoteStepper] = useState(() => searchParams.get('new') === 'true');

  useEffect(() => {
    loadQuoteRequests();
    loadQuoteDocuments();
  }, []);

  useEffect(() => {
    if (searchParams.get('tab') === 'offertes') {
      setActiveTab('offertes');
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('tab');
      if (searchParams.get('new') === 'true') {
        setShowQuoteStepper(true);
        newParams.delete('new');
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || quoteRequests.length === 0) return;
    const quote = quoteRequests.find(q => q.id === openId);
    if (quote) {
      setSelectedQuote(quote);
      setIsDetailsOpen(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('open');
      setSearchParams(newParams, { replace: true });
    }
  }, [quoteRequests, searchParams, setSearchParams]);

  const loadQuoteRequests = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('quote_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setQuoteRequests(data || []);
    } catch (error) {
      console.error('Error loading quote requests:', error);
      toast.error('Fout bij het laden van offerteaanvragen');
    } finally {
      setIsLoading(false);
    }
  };

  const loadQuoteDocuments = async () => {
    setQuoteDocsLoading(true);
    try {
      const { data, error } = await supabase
        .from('admin_documents')
        .select('*')
        .eq('document_type', 'quote')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setQuoteDocuments(data || []);
    } catch (error) {
      console.error('Error loading quotes:', error);
    } finally {
      setQuoteDocsLoading(false);
    }
  };

  const handleQuoteDocStatusChange = async (id: string, newStatus: string) => {
    try {
      const updateData: Record<string, any> = {
        status: newStatus,
        updated_at: new Date().toISOString(),
      };
      if (newStatus === 'sent') {
        updateData.sent_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('admin_documents')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      setQuoteDocuments((prev) =>
        prev.map((d) => (d.id === id ? { ...d, status: newStatus, sent_at: updateData.sent_at ?? d.sent_at } : d))
      );
      toast.success('Status bijgewerkt');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Kon status niet bijwerken');
    }
  };

  const handleQuoteStepperComplete = () => {
    setShowQuoteStepper(false);
    loadQuoteDocuments();
  };

  const updateQuoteStatus = async (quoteId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('quote_requests')
        .update({ status: newStatus })
        .eq('id', quoteId);

      if (error) throw error;
      await loadQuoteRequests();
      toast.success('Status bijgewerkt');

      if (selectedQuote?.id === quoteId) {
        setSelectedQuote({ ...selectedQuote, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating quote status:', error);
      toast.error('Fout bij het bijwerken van de status');
    }
  };

  const handleDeleteClick = (quote: QuoteRequest) => {
    setQuoteToDelete(quote);
    setDeleteDialogOpen(true);
  };

  const deleteQuote = async () => {
    if (!quoteToDelete) return;

    try {
      setIsDeleting(true);
      const { error } = await supabase
        .from('quote_requests')
        .delete()
        .eq('id', quoteToDelete.id);

      if (error) throw error;

      toast.success('Offerteaanvraag verwijderd');
      setDeleteDialogOpen(false);
      setQuoteToDelete(null);
      await loadQuoteRequests();

      if (selectedQuote?.id === quoteToDelete.id) {
        setIsDetailsOpen(false);
        setSelectedQuote(null);
      }
    } catch (error) {
      console.error('Error deleting quote:', error);
      toast.error('Fout bij het verwijderen');
    } finally {
      setIsDeleting(false);
    }
  };

  const viewQuoteDetails = (quote: QuoteRequest) => {
    setSelectedQuote(quote);
    setIsDetailsOpen(true);
  };

  const filteredQuotes = quoteRequests.filter(quote => {
    if (statusFilter === 'all') return true;
    return quote.status === statusFilter;
  });

  const stats = {
    total: quoteRequests.length,
    new: quoteRequests.filter(q => q.status === 'new').length,
    inProgress: quoteRequests.filter(q => q.status === 'in_progress').length,
    completed: quoteRequests.filter(q => q.status === 'completed').length,
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-6 py-12">
          <Breadcrumb pageName="Offertes & Aanvragen" />
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-jv-primary mb-2">Offertes & Aanvragen</h1>
            <p className="text-gray-600">Beheer klant offerteaanvragen en genereer offertes</p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="aanvragen">Aanvragen</TabsTrigger>
              <TabsTrigger value="offertes">Offertes</TabsTrigger>
            </TabsList>

            <TabsContent value="offertes" className="mt-6">
              {showQuoteStepper ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-center justify-between mb-8">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Nieuwe offerte</h2>
                      <p className="text-sm text-gray-500 mt-1">Maak een offerte aan en verstuur deze naar de klant</p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setShowQuoteStepper(false)}
                      className="gap-2"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Terug naar overzicht
                    </Button>
                  </div>

                  <DocumentStepper
                    documentType="quote"
                    onComplete={handleQuoteStepperComplete}
                    onCancel={() => setShowQuoteStepper(false)}
                  />
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                        <FileSpreadsheet className="w-6 h-6 text-jv-primary" />
                        Gegenereerde offertes
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">{quoteDocuments.length} offerte{quoteDocuments.length !== 1 ? 's' : ''}</p>
                    </div>
                    <Button
                      onClick={() => setShowQuoteStepper(true)}
                      className="bg-jv-primary hover:bg-jv-primary/90 gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Nieuwe offerte
                    </Button>
                  </div>

                  <DocumentList documents={quoteDocuments} loading={quoteDocsLoading} onStatusChange={handleQuoteDocStatusChange} onSent={loadQuoteDocuments} />
                </motion.div>
              )}
            </TabsContent>

            <TabsContent value="aanvragen" className="mt-6">

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="border border-[#e7e1d6] bg-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#5a6552] mb-1">Totaal</p>
                    <p className="text-3xl font-bold text-[#1e2b22]">{stats.total}</p>
                  </div>
                  <div className="bg-[#e7e1d6] p-3 rounded-lg">
                    <FileText className="w-6 h-6 text-[#1e2b22]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-[#e7e1d6] bg-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#5a6552] mb-1">Nieuw</p>
                    <p className="text-3xl font-bold text-[#5a6552]">{stats.new}</p>
                  </div>
                  <div className="bg-[#e7e1d6] p-3 rounded-lg">
                    <Clock className="w-6 h-6 text-[#5a6552]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-[#e7e1d6] bg-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#5a6552] mb-1">In behandeling</p>
                    <p className="text-3xl font-bold text-[#c8a94a]">{stats.inProgress}</p>
                  </div>
                  <div className="bg-[#f5eede] p-3 rounded-lg">
                    <Loader2 className="w-6 h-6 text-[#c8a94a]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-[#e7e1d6] bg-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#5a6552] mb-1">Voltooid</p>
                    <p className="text-3xl font-bold text-[#1e2b22]">{stats.completed}</p>
                  </div>
                  <div className="bg-[#e7e1d6] p-3 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-[#1e2b22]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="border-0 shadow-sm mb-6">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Filter op status
                  </label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full sm:w-64">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle statussen</SelectItem>
                      <SelectItem value="new">Nieuw</SelectItem>
                      <SelectItem value="in_progress">In behandeling</SelectItem>
                      <SelectItem value="completed">Voltooid</SelectItem>
                      <SelectItem value="cancelled">Geannuleerd</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-gray-600 self-end">
                  {filteredQuotes.length} {filteredQuotes.length === 1 ? 'resultaat' : 'resultaten'}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quote Requests Table */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Offerteaanvragen</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : filteredQuotes.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Geen offerteaanvragen gevonden</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Datum</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Klant</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Plant</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Aantal</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Bezorging</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Aanplant</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Acties</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredQuotes.map((quote) => (
                        <tr key={quote.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-4 px-4 text-sm text-gray-600">
                            {formatDate(quote.created_at)}
                          </td>
                          <td className="py-4 px-4">
                            <div>
                              <p className="font-medium text-gray-900">{quote.name}</p>
                              <p className="text-sm text-gray-600">{quote.email || '-'}</p>
                              {quote.request_type === 'price_match' && (
                                <Badge className="mt-1 bg-amber-500 text-white">Elders goedkoper</Badge>
                              )}
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div>
                              <p className="font-medium text-gray-900">{quote.plant_name}</p>
                              <p className="text-sm text-gray-600">{quote.plant_height}</p>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-gray-900">{quote.quantity}</td>
                          <td className="py-4 px-4 text-sm text-gray-600">
                            {deliveryMethodLabels[quote.delivery_method]}
                          </td>
                          <td className="py-4 px-4">
                            {quote.aanplantservice ? (
                              <Badge className="bg-green-500 text-white">Ja</Badge>
                            ) : (
                              <span className="text-sm text-gray-400">Nee</span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            <Select
                              value={quote.status}
                              onValueChange={(value) => updateQuoteStatus(quote.id, value)}
                            >
                              <SelectTrigger className="w-40">
                                <Badge className={`${statusColors[quote.status]} text-white`}>
                                  {statusLabels[quote.status]}
                                </Badge>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">Nieuw</SelectItem>
                                <SelectItem value="in_progress">In behandeling</SelectItem>
                                <SelectItem value="completed">Voltooid</SelectItem>
                                <SelectItem value="cancelled">Geannuleerd</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => viewQuoteDetails(quote)}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Bekijk
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteClick(quote)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

            </TabsContent>
          </Tabs>
        </div>

      {/* Details Dialog */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Offerteaanvraag Details</DialogTitle>
          </DialogHeader>
          {selectedQuote && (
            <div className="space-y-6">
              {/* Status */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Status</label>
                <Select
                  value={selectedQuote.status}
                  onValueChange={(value) => updateQuoteStatus(selectedQuote.id, value)}
                >
                  <SelectTrigger className="w-64">
                    <Badge className={`${statusColors[selectedQuote.status]} text-white`}>
                      {statusLabels[selectedQuote.status]}
                    </Badge>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">Nieuw</SelectItem>
                    <SelectItem value="in_progress">In behandeling</SelectItem>
                    <SelectItem value="completed">Voltooid</SelectItem>
                    <SelectItem value="cancelled">Geannuleerd</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Customer Info */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Klantgegevens
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Naam</p>
                    <p className="font-medium">{selectedQuote.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">E-mail</p>
                    <p className="font-medium">{selectedQuote.email || '-'}</p>
                  </div>
                  {selectedQuote.phone && (
                    <div>
                      <p className="text-sm text-gray-600">Telefoon</p>
                      <p className="font-medium">{selectedQuote.phone}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-600">Postcode</p>
                    <p className="font-medium">{selectedQuote.postal_code}</p>
                  </div>
                </div>
              </div>

              {/* Plant Details */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Plantgegevens
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Plant</p>
                    <p className="font-medium">{selectedQuote.plant_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Hoogte</p>
                    <p className="font-medium">{selectedQuote.plant_height}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Aantal</p>
                    <p className="font-medium">{selectedQuote.quantity}</p>
                  </div>
                </div>
              </div>

              {/* Delivery Info */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Bezorging
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Bezorgmethode</p>
                    <p className="font-medium">{deliveryMethodLabels[selectedQuote.delivery_method]}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Contact via</p>
                    <p className="font-medium">{contactMethodLabels[selectedQuote.preferred_contact_method]}</p>
                  </div>
                </div>
              </div>

              {/* Price Match Details */}
              {selectedQuote.request_type === 'price_match' && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Tag className="w-5 h-5" />
                    Elders goedkoper
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Concurrent URL</p>
                      <a href={selectedQuote.competitor_url || '#'} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 hover:underline break-all">
                        {selectedQuote.competitor_url}
                      </a>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Prijs bij concurrent</p>
                      <p className="font-medium">€{Number(selectedQuote.competitor_price || 0).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Aanplantservice */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Sprout className="w-5 h-5" />
                  Aanplantservice
                </h3>
                <div>
                  {selectedQuote.aanplantservice ? (
                    <Badge className="bg-green-500 text-white text-sm px-3 py-1">Ja, klant wil aanplantservice</Badge>
                  ) : (
                    <span className="text-gray-600">Nee</span>
                  )}
                </div>
              </div>

              {/* Optional Message */}
              {selectedQuote.optional_message && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Bericht van klant
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedQuote.optional_message}</p>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="border-t pt-6">
                <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                  <div>
                    <p className="text-gray-500">Aangemaakt op</p>
                    <p>{formatDate(selectedQuote.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Laatst bijgewerkt</p>
                    <p>{formatDate(selectedQuote.updated_at)}</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between border-t pt-6">
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDeleteClick(selectedQuote)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Verwijderen
                </Button>
                <Button onClick={() => setIsDetailsOpen(false)}>
                  Sluiten
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Offerteaanvraag verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je deze offerteaanvraag wilt verwijderen? Deze actie kan niet
              ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteQuote}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verwijderen...
                </>
              ) : (
                'Verwijderen'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
